import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import ReactTable from "react-table";
import "react-table/react-table.css";

import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";

const CheckboxTableManual = (props) => {
  const {
    keyField,
    allowSelectAll,
    selectAll,
    loadDataAction,
    loadDataParams,
    vScroll = '',
    handler = null,
    loadingData = false,
    page,
    onPageChange,
  } = props;

  const dispatch = useDispatch();

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const [previousDataTableState, setPreviousDataTableState] = useState({
    page: 0,
    pageSize: 20,
    sorted: [],
    filtered: [],
  });

  // if the parent passes a page, it owns the page number rather than react-table (see pageRef below)
  const controlledPage = page !== undefined;

  // react-table resolves its state as { ...state, ...props }, so a page passed in the props always wins
  // over its internal state. It calls onPageChange and then onFetchData synchronously, before the parent
  // has had a chance to re-render, which means onFetchData would otherwise be handed the previous page.
  // Tracking the page in a ref gives us a value that is always current at the point we dispatch.
  const pageRef = useRef(page ?? 0);

  // records the page the pager last moved to, so that we can tell a page change made here apart from
  // one the parent made on its own
  const pagerPage = useRef(null);

  const handlePageChange = (newPage) => {
    pageRef.current = newPage;
    pagerPage.current = newPage;
    onPageChange?.(newPage);
  };

  // keep the ref (and the change-detection state) in step when the parent moves the page itself,
  // e.g. resetting to the first page after an external filter has been applied
  useEffect(() => {
    if (!controlledPage) return;
    pageRef.current = page;
    if (pagerPage.current === page) {
      // the pager made this change, so a fetch is already on its way and will record the new state itself.
      // updating it here would make the change-detection below think nothing had changed, and no data would load
      pagerPage.current = null;
      return;
    }
    // the parent moved the page, which means it has loaded the data for that page itself,
    // so record it here to stop us requesting the same data a second time
    setPreviousDataTableState((prev) => prev.page === page ? prev : { ...prev, page });
  }, [page, controlledPage])

  const { darkMode } = useSelector((state) => state.app)

  const isSelected = props.isSelected // if a custom isSelected function is passed in the props,
    ? props.isSelected                // use that,
    : (key) => {                      // otherwise assume we're maintaining an array of selected keys - we should have the array passed in the props (selection)
        const { selection, selectAll } = props;
        if (selectAll) {
          if (selection) { // if the selectAll flag is true, then the selection array becomes an exception array, i.e. it's listing those that aren't selected
            return !selection.includes(key);
          } else return true;
        } else if (selection) {
          return selection.includes(key);
        } else return false;
      };

  const toggleSelection = props.toggleSelection // if a custom toggleSelection function is passed in the props,
    ? props.toggleSelection                     // use that,
    : (key) => {                                // otherwise maintain an array of selected keys -
                                                // we should have the array (selection) and the setter (setSelection) passed in the props
        let { selection } = props;
        const { setSelection } = props;

        if (selection && setSelection) {
          const keyIndex = selection.indexOf(key);

          if (keyIndex >= 0) {
            selection = [
              ...selection.slice(0, keyIndex),
              ...selection.slice(keyIndex + 1),
            ];
          } else {
            selection.push(key);
          }

          setSelection(selection);
        }
      }

  const toggleAll = props.toggleAll // if a custom toggleAll function is passed in the props,
    ? props.toggleAll               // use that,
    : () => {                       // otherwise toggle the selectAll flag and clear all selections
        const { selectAll, setSelection, setSelectAll } = props;

        if (setSelectAll) {
          setSelectAll(!selectAll);
        }

        if (setSelection) {
          setSelection([]);
        }
      }

  const getTrProps = props.getTrProps // if a custom getTrProps function is passed in the props,
    ? props.getTrProps                // use that,
    : (state, row) => {               // otherwise return styling according to whether the row has been checked or not
        const selected = row?.original?.id ? isSelected(row?.original?.id) : false;
        return {
          style: {
            backgroundColor: selected
              ? darkMode ? "#204183" : "#d6e8fa"
              : "inherit",
          },
        }
      }

  const columns = [
    {
      // create a checkbox column as the first column
      key: "CHECKBOXTABLE_CHECKBOX",
      Header: () => {
        if (allowSelectAll) {
          return <input
            type="checkbox"
            checked={selectAll}
            disabled={loadingData}
            onChange={toggleAll}
          />
        } else return null;
      },
      id: "checkboxtable_checkbox",
      width: 30,
      accessor: null,
      resizable: false,
      filterable: false,
      Cell: ({row}) => {
        const key = row[keyField];
        return <input
          type="checkbox"
          checked={isSelected(key)}
          disabled={loadingData}
          onChange={() => {
            if (key) {
              toggleSelection(key);
            }
          }}
        />
      }
    },
    // add all the other columns
    ...props.columns,
  ]

  // seed the parent with the initial table state, so that it has something to work with
  // before the first fetch has happened
  useEffect(() => {
    if (initialLoadComplete) return;
    handler?.({
      page: previousDataTableState?.page,
      pageSize: previousDataTableState?.pageSize,
      sorted: previousDataTableState?.sorted,
      filtered: previousDataTableState?.filtered,
    });
    setInitialLoadComplete(true);
  }, [previousDataTableState, handler, initialLoadComplete])

  return (
    <>
      <ReactTable
        style={vScroll ? {
          height: `${vScroll}px`,
          maxHeight: `${vScroll}px`,
          overflow: 'auto',
        } : undefined}
        {...props}
        manual
        columns={columns}
        getTrProps={getTrProps}
        onPageChange={handlePageChange}
        onFetchData={debounce((state, instance) => {
          if (state.data) {
            // to prevent hitting the endpoint unnecessarily, first check that there has actually been a change in state
            const dataTableState = {
              page: controlledPage ? pageRef.current : state.page,
              pageSize: state.pageSize,
              sorted: state.sorted,
              filtered: state.filtered,
            }
            if (!isEqual(dataTableState, previousDataTableState)) {
              let sortField = null;
              let sortDirection = null;

              if (state.sorted && state.sorted[0]) {
                sortField = state.sorted[0].id;
                sortDirection = state.sorted[0].desc ? "desc" : "asc";
              }

              // optional handler to allow the parent component to do something with the new table state
              // (e.g. store it in its own state, or use it to trigger some other action) whenever the table state changes
              handler?.(dataTableState);

              dispatch(
                loadDataAction({
                  ...loadDataParams,
                  pageSize: state.pageSize,
                  requestPageNumber: dataTableState.page + 1, // tells the backend which page to serve (starts at 1, like normal page numbering)
                  sortField: sortField,
                  sortDirection: sortDirection,
                  filters: state.filtered,
                })
              );

              setPreviousDataTableState(dataTableState);
            }
          }
        }, 300)} // debounce period
      />
    </>
  )
}

export default CheckboxTableManual;
